import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ReviewModal({ open, onOpenChange, appointmentId, doctorName, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Please pick a star rating.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/patient/appointments/${appointmentId}/review`, {
        rating,
        comment: comment.trim() || null,
      });
      toast.success("Thanks! Your review was submitted.");
      onSubmitted?.(data);
      onOpenChange(false);
      setRating(0);
      setComment("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-modal" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Rate your consultation</DialogTitle>
          <DialogDescription>
            How was your experience with {doctorName || "your doctor"}? Your feedback helps other patients.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110"
                aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
              >
                <Star
                  className={
                    "h-9 w-9 transition-colors " +
                    (active ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40")
                  }
                />
              </button>
            );
          })}
        </div>

        <Textarea
          data-testid="review-comment-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share details about your consultation (optional)"
          rows={4}
          className="rounded-xl resize-none"
        />

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="review-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || rating < 1}
            data-testid="review-submit-btn"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
          >
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
